import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrophy, faMicrophone, faComment, faClock, faMedal, faCrown } from '@fortawesome/free-solid-svg-icons';
import * as discordApi from '../services/discordApi';
import { DiscordMember } from '../services/discordApi';

interface LeaderboardEntry {
  member: DiscordMember;
  score: number;
  rank: number;
}

const Leaderboards: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'voice' | 'activity' | 'roles'>('voice');
  const [voiceLeaders, setVoiceLeaders] = useState<LeaderboardEntry[]>([]);
  const [activityLeaders, setActivityLeaders] = useState<LeaderboardEntry[]>([]);
  const [roleLeaders, setRoleLeaders] = useState<LeaderboardEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchLeaderboardData = async () => {
    try {
      setError(null);
      const [membersResponse, voiceResponse] = await Promise.all([
        discordApi.getOnlineMembers(),
        discordApi.getVoiceChannels(),
      ]);

      if (membersResponse.success && membersResponse.data) {
        const members = membersResponse.data;

        // Voice Leaderboard - members currently in voice
        if (voiceResponse.success && voiceResponse.data) {
          const voiceMembers = new Set<string>();
          voiceResponse.data.forEach(channel => {
            channel.members.forEach(member => {
              voiceMembers.add(member.id);
            });
          });

          const voiceEntries: LeaderboardEntry[] = members
            .filter(m => voiceMembers.has(m.id))
            .map((member, index) => ({
              member,
              score: 1, // Currently just showing who's in voice
              rank: index + 1,
            }));

          setVoiceLeaders(voiceEntries);
        }

        // Activity Leaderboard - based on number of activities
        const activityEntries: LeaderboardEntry[] = members
          .filter(m => m.activities && m.activities.length > 0)
          .map(member => ({
            member,
            score: member.activities?.length || 0,
            rank: 0,
          }))
          .sort((a, b) => b.score - a.score)
          .map((entry, index) => ({
            ...entry,
            rank: index + 1,
          }));

        setActivityLeaders(activityEntries);

        // Role Leaderboard - based on number of roles
        const roleEntries: LeaderboardEntry[] = members
          .map(member => ({
            member,
            score: member.roles?.length || 0,
            rank: 0,
          }))
          .sort((a, b) => b.score - a.score)
          .slice(0, 20) // Top 20
          .map((entry, index) => ({
            ...entry,
            rank: index + 1,
          }));

        setRoleLeaders(roleEntries);
      } else {
        throw new Error(membersResponse.error || 'Failed to fetch members');
      }

      setLoading(false);
    } catch (err) {
      console.error('Error fetching leaderboard data:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLeaderboardData();
    // Refresh every 5 minutes
    const interval = setInterval(fetchLeaderboardData, 300000);
    return () => clearInterval(interval);
  }, []);

  const getRankIcon = (rank: number) => {
    if (rank === 1) return <FontAwesomeIcon icon={faCrown} className="text-[#ffd803]" />;
    if (rank === 2) return <FontAwesomeIcon icon={faMedal} className="text-gray-400" />;
    if (rank === 3) return <FontAwesomeIcon icon={faMedal} className="text-[#cd7f32]" />;
    return <span className="font-black text-lg">#{rank}</span>;
  };

  const renderLeaderboard = (entries: LeaderboardEntry[], scoreLabel: string) => {
    if (entries.length === 0) {
      return (
        <div className="bg-white border-4 border-black p-8 text-center">
          <p className="text-xl font-black">No data available</p>
        </div>
      );
    }

    return (
      <div className="space-y-3">
        {entries.map((entry) => (
          <div
            key={entry.member.id}
            className={`border-4 border-black p-4 flex items-center gap-4 ${
              entry.rank === 1
                ? 'bg-[#ffd803]'
                : entry.rank === 2
                ? 'bg-gray-200'
                : entry.rank === 3
                ? 'bg-[#cd7f32] bg-opacity-30'
                : 'bg-white'
            }`}
          >
            {/* Rank */}
            <div className="w-12 text-center flex-shrink-0">
              {getRankIcon(entry.rank)}
            </div>

            {/* Avatar */}
            <img
              src={discordApi.getAvatarUrl(entry.member)}
              alt={entry.member.username}
              className="w-12 h-12 rounded-full border-2 border-black flex-shrink-0"
            />

            {/* Member Info */}
            <div className="flex-1 min-w-0">
              <p className="font-black text-lg truncate">
                {entry.member.displayName || entry.member.username}
              </p>
              <p className="text-sm font-bold text-gray-700">
                @{entry.member.username}
              </p>
            </div>

            {/* Score */}
            <div className="bg-[#9b5de5] border-2 border-black px-4 py-2 flex-shrink-0">
              <p className="font-black text-white text-xl">{entry.score}</p>
              <p className="font-bold text-white text-xs">{scoreLabel}</p>
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[#f582ae] p-8 lg:ml-64 lg:mr-80">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="bg-[#ffd803] border-8 border-black p-8 mb-8 shadow-[12px_12px_0px_0px_rgba(0,0,0,1)]">
          <h1 className="text-6xl font-black uppercase mb-4 flex items-center gap-4">
            <FontAwesomeIcon icon={faTrophy} />
            Leaderboards
          </h1>
          <p className="text-xl font-bold">
            Who's dominating the server?
          </p>
        </div>

        {/* Tabs */}
        <div className="grid grid-cols-3 gap-4 mb-8">
          <button
            onClick={() => setActiveTab('voice')}
            className={`border-4 border-black p-4 font-black uppercase transition-all ${
              activeTab === 'voice'
                ? 'bg-[#00bbf9] translate-y-1 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]'
                : 'bg-white hover:translate-y-1 shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] hover:shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]'
            }`}
          >
            <FontAwesomeIcon icon={faMicrophone} className="mb-2 text-2xl block" />
            Voice
          </button>
          <button
            onClick={() => setActiveTab('activity')}
            className={`border-4 border-black p-4 font-black uppercase transition-all ${
              activeTab === 'activity'
                ? 'bg-[#00bbf9] translate-y-1 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]'
                : 'bg-white hover:translate-y-1 shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] hover:shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]'
            }`}
          >
            <FontAwesomeIcon icon={faComment} className="mb-2 text-2xl block" />
            Activity
          </button>
          <button
            onClick={() => setActiveTab('roles')}
            className={`border-4 border-black p-4 font-black uppercase transition-all ${
              activeTab === 'roles'
                ? 'bg-[#00bbf9] translate-y-1 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]'
                : 'bg-white hover:translate-y-1 shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] hover:shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]'
            }`}
          >
            <FontAwesomeIcon icon={faMedal} className="mb-2 text-2xl block" />
            Roles
          </button>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="bg-white border-8 border-black p-8 text-center shadow-[8px_8px_0px_0px_rgba(0,0,0,1)]">
            <p className="text-2xl font-black">Loading leaderboards...</p>
          </div>
        )}

        {/* Error State */}
        {error && !loading && (
          <div className="bg-[#ff6b6b] border-8 border-black p-8 shadow-[8px_8px_0px_0px_rgba(0,0,0,1)]">
            <p className="text-2xl font-black mb-2">⚠️ Error</p>
            <p className="font-bold">{error}</p>
          </div>
        )}

        {/* Leaderboard Content */}
        {!loading && !error && (
          <div className="bg-[#b8f2e6] border-8 border-black p-6 shadow-[8px_8px_0px_0px_rgba(0,0,0,1)]">
            {activeTab === 'voice' && (
              <>
                <h2 className="text-3xl font-black uppercase mb-4 flex items-center gap-2">
                  <FontAwesomeIcon icon={faMicrophone} />
                  Currently in Voice
                </h2>
                {renderLeaderboard(voiceLeaders, 'IN VOICE')}
              </>
            )}

            {activeTab === 'activity' && (
              <>
                <h2 className="text-3xl font-black uppercase mb-4 flex items-center gap-2">
                  <FontAwesomeIcon icon={faComment} />
                  Most Active
                </h2>
                {renderLeaderboard(activityLeaders, 'ACTIVITIES')}
              </>
            )}

            {activeTab === 'roles' && (
              <>
                <h2 className="text-3xl font-black uppercase mb-4 flex items-center gap-2">
                  <FontAwesomeIcon icon={faMedal} />
                  Most Roles
                </h2>
                {renderLeaderboard(roleLeaders, 'ROLES')}
              </>
            )}
          </div>
        )}

        {/* Info Banner */}
        <div className="mt-8 bg-[#9b5de5] border-4 border-black p-4 text-white">
          <p className="font-bold text-center">
            <FontAwesomeIcon icon={faClock} className="mr-2" />
            Leaderboards update every 5 minutes • More stats coming soon!
          </p>
        </div>
      </div>
    </div>
  );
};

export default Leaderboards;
